import React, { useCallback, useEffect, useState } from 'react';
import { Paper, Button, makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { BaseEntityField, BaseInfoCompActionsSet, BaseInfoCompProps, BaseInfoCompSaveAction, InfoCompPanelHeader, usePropValue,
    inputStateSetterHelper, useModePanelStyle, useModeFormStyle, FontIcon, ModeInfoCompAutoCompleteInputField, ModeInfoCompSelectInputField,
    ModeInfoCompTextInputField, ModeInfoCompImageInputField, ModeFormInputSection, FieldInputType, ModeInfoCompCheckBoxInputField,
    ModeInfoCompTextAreaInputField } from '@moderepo/mode-app-components';
import { Entity, EntityAttributeValueType } from '@moderepo/mode-internal-apis';
import { EntityInputComp, EntityInputField } from './EntityInputComp';



const useStyle = makeStyles(() => {
    return {
        root: {
        },
    };
}, {
    name: 'EntityInfoComp', index: 1,
});



/**
 * This is the interface of the set of input fields' values that will be passed to the onClick handler when the 'saveUpdate' button is clicked.
 */
export interface EntityInfoUpdatableInputs {
    readonly parent?: Entity | undefined;
    readonly entityId?: string | undefined;
    readonly homeId?: number | undefined;
    readonly entityClass?: string | undefined;
    readonly timeZone?: string | undefined;
    readonly name?: string | undefined;
    readonly description?: string | undefined;

    // NOTE: File object means new file selected. null means image is deleted. undefined means no change.
    readonly iconUrl?: File | null | undefined;
    readonly imageUrl?: File | null | undefined;

    readonly attributes?: {
        [key: string]: EntityAttributeValueType | undefined;
    } | undefined;
}


/**
 * This component has a custom saveUpdate button's onClick input params therefore we need to extends the BaseInfoCompActionsSet and
 * override the 'saveUpdate' action.
 */
export interface EntityInfoCompActionsSet extends BaseInfoCompActionsSet {
    readonly saveUpdate: BaseInfoCompSaveAction<EntityInfoUpdatableInputs>
}


export interface EntityInfoCompProps extends BaseInfoCompProps {
    readonly fields: {
        readonly entityId: BaseEntityField<string>;
        readonly name: BaseEntityField<string>;
        readonly description: BaseEntityField<string>;
        readonly iconFileName: BaseEntityField<string>;
        readonly iconUrl: BaseEntityField<string>;
        readonly imageFileName: BaseEntityField<string>;
        readonly imageUrl: BaseEntityField<string>;
        readonly entityClass: BaseEntityField<string>;
        readonly projectId?: BaseEntityField<string>;
        readonly homeId: BaseEntityField<number>;
        readonly parent: EntityInputField;
        readonly attributes?: {
            [attributeKey: string]: BaseEntityField<EntityAttributeValueType> | undefined;
        };
        readonly timeZone: BaseEntityField<string>;
        readonly createdAt: BaseEntityField<string>;
        readonly updatedAt: BaseEntityField<string>;
    }

    readonly actions?: EntityInfoCompActionsSet | undefined;
    readonly texts: {
        readonly iconGroupTitle: string;
        readonly imageGroupTitle: string;
        readonly attributesGroupTitle: string;
    }

    readonly onFormInputChange?: (fieldName: string, value: any, isAttribute?: boolean)=> void;
    readonly onFieldClicked?: (fieldName: string, isAttribute?: boolean)=> void;
}



/**
 * This is a DUMB component used for displaying Entity info. This component does not have any logic. All it does it display
 * data based on the props provided by the container. All action done in this component will be dispatched back to the
 * container's handler.
 */
export const EntityInfoComp: React.FC<EntityInfoCompProps> = (props: EntityInfoCompProps) => {
    const [parentInput, setParentInput] = usePropValue<Entity | undefined>(props.fields.parent?.value);
    const [entityIdInput, setEntityIdInput] = usePropValue<string | undefined>(props.fields.entityId.value);
    const [homeIdInput, setHomeIdInput] = usePropValue<number | undefined>(props.fields.homeId.value);
    const [entityClassInput, setEntityClassInput] = usePropValue<string | undefined>(props.fields.entityClass.value);
    const [timeZoneInput, setTimeZoneInput] = usePropValue<string | undefined>(props.fields.timeZone?.value);
    const [nameInput, setNameInput] = usePropValue<string | undefined>(props.fields.name.value);
    const [descriptionInput, setDescriptionInput] = usePropValue<string | undefined>(props.fields.description.value);
    const [iconUrlInput, setIconUrlInput] = usePropValue<string | File | null | undefined>(props.fields.iconUrl.value);
    const [imageUrlInput, setImageUrlInput] = usePropValue<string | File | null | undefined>(props.fields.imageUrl.value);

    // Local state to store the inputs for attributes
    const [attributeInputs, setAttributeInputs] = useState<{
        [key: string]: EntityAttributeValueType | undefined;
    } | undefined>();


    /**
     * Monitor props.fields.attributes. If props.fields.attributes changed, we need to reinitialize attributeInputs values
     * based on props.fields.attributes.
     */
    useEffect(() => {
        if (props.fields.attributes) {
            const initialAttributeInputValues = {};
            Object.entries(props.fields.attributes).forEach(([key, inputField]) => {
                initialAttributeInputValues[key] = inputField?.value;
            });
            setAttributeInputs(initialAttributeInputValues);
        } else {
            setAttributeInputs(undefined);
        }
    }, [props.fields.attributes]);


    /**
     * On input change handler for all the inputs. When an input change, call the onInputChangeHelper and pass it the setter functions
     * for all inputs. The helper function will call the setter function accordingly based on the input name.
     */
    const onInputChange = useCallback((inputName: string, value: any) => {
        inputStateSetterHelper(inputName, value, {
            parent     : setParentInput,
            entityId   : setEntityIdInput,
            homeId     : setHomeIdInput,
            entityClass: setEntityClassInput,
            timeZone   : setTimeZoneInput,
            name       : setNameInput,
            description: setDescriptionInput,
            iconUrl    : setIconUrlInput,
            imageUrl   : setImageUrlInput,
        });
    }, [setDescriptionInput, setEntityClassInput, setEntityIdInput, setHomeIdInput, setImageUrlInput, setNameInput, setParentInput,
        setTimeZoneInput, setIconUrlInput]);


    /**
     * On attribute input change, update the attributeInputs state.
     */
    const onAttributeInputChange = useCallback((key: string, value: any) => {
        setAttributeInputs((currentInputValues) => {
            return {
                ...currentInputValues,
                [key]: value,
            };
        });
    }, []);


    const panelClasses = useModePanelStyle();
    const formClasses = useModeFormStyle();
    const classes = useStyle();


    /**
     * On cancel edit, we need to reverse all the input back to the original values
     */
    const reverseFormInput = useCallback(() => {
        setParentInput(props.fields.parent.value);
        setEntityIdInput(props.fields.entityId.value);
        setHomeIdInput(props.fields.homeId.value);
        setEntityClassInput(props.fields.entityClass.value);
        setTimeZoneInput(props.fields.timeZone.value);
        setNameInput(props.fields.name.value);
        setDescriptionInput(props.fields.description.value);
        setIconUrlInput(props.fields.iconUrl.value);
        setImageUrlInput(props.fields.imageUrl.value);

        if (props.fields.attributes) {
            const initialAttributeInputValues = {};
            Object.entries(props.fields.attributes).forEach(([key, inputField]) => {
                initialAttributeInputValues[key] = inputField?.value;
            });
            setAttributeInputs(initialAttributeInputValues);
        }
    }, [setParentInput, props.fields.parent.value, props.fields.entityId.value, props.fields.homeId.value, props.fields.entityClass.value,
        props.fields.timeZone.value, props.fields.name.value, props.fields.description.value, props.fields.iconUrl.value, props.fields.imageUrl.value,
        props.fields.attributes, setEntityIdInput, setHomeIdInput, setEntityClassInput, setTimeZoneInput, setNameInput, setDescriptionInput,
        setIconUrlInput, setImageUrlInput]);


    /**
     * On form submit. Collect the values for all input and pass it back to the container
     */
    const onFormSubmit = useCallback(async (event: React.FormEvent) => {
        event.stopPropagation();
        event.preventDefault();

        if (props.actions?.saveUpdate) {
            props.actions.saveUpdate.onClick({
                parent     : parentInput,
                entityId   : entityIdInput,
                homeId     : homeIdInput,
                entityClass: entityClassInput,
                timeZone   : timeZoneInput,
                name       : nameInput?.trim(),
                description: descriptionInput?.trim(),
                // If iconUrl and imageUrl are type File, we know the user selected a new file therefore we will pass them to the
                // handler. If they are null then that mean the user wanted to delete the images so we pass null to the handler.
                // Otherwise, we pass undefined which mean no change.
                iconUrl    : iconUrlInput instanceof File || iconUrlInput === null ? iconUrlInput : undefined,
                imageUrl   : imageUrlInput instanceof File || imageUrlInput === null ? imageUrlInput : undefined,
                attributes : attributeInputs,
            });
        }

        return false;
    }, [props.actions?.saveUpdate, parentInput, entityIdInput, homeIdInput, entityClassInput, timeZoneInput, nameInput, descriptionInput,
        iconUrlInput, imageUrlInput, attributeInputs]);


    return (
        <Paper
            elevation={2}
            className={clsx(panelClasses.root, props.className, classes.root)}
        >
            <InfoCompPanelHeader {...props} />
            <div className={clsx(panelClasses.panelContent)}>
                <form
                    className={clsx(formClasses.root, props.formProps?.className)}
                    noValidate
                    autoComplete="on"
                    method="POST"
                    onSubmit={onFormSubmit}
                >
                    <div className={clsx(formClasses.formInputGroup)}>
                        {props.fields.projectId && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.projectId}
                                fieldName="projectId"
                                inputValue={props.fields.projectId.displayValue ?? props.fields.projectId.value}
                                onInputChange={onInputChange}
                            />
                        )}
                        <ModeInfoCompSelectInputField
                            className={formClasses.formInputField}
                            compProps={props}
                            inputProps={props.fields.homeId}
                            fieldName="homeId"
                            inputValue={props.fields.homeId.editable
                                ? homeIdInput : props.fields.homeId.displayValue ?? props.fields.homeId.value}
                            onInputChange={onInputChange}
                        />
                        <EntityInputComp
                            className={formClasses.formInputField}
                            fieldName="parent"
                            isEditing={props.isEditing}
                            {...props.fields.parent}
                            value={parentInput}
                            onFieldClicked={props.onFieldClicked}
                            onFormInputChange={(fieldName: string, entity: Entity | undefined) => {
                                // Save the input locally
                                onInputChange(fieldName, entity);

                                // notify the container input changed
                                if (props.onFormInputChange) {
                                    props.onFormInputChange(fieldName, entity);
                                }
                            }}
                        />
                        <ModeInfoCompSelectInputField
                            className={formClasses.formInputField}
                            compProps={props}
                            inputProps={props.fields.entityClass}
                            fieldName="entityClass"
                            inputValue={props.fields.entityClass.editable
                                ? entityClassInput : props.fields.entityClass.displayValue ?? props.fields.entityClass.value}
                            onInputChange={onInputChange}
                        />
                        {props.fields.entityId && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.entityId}
                                fieldName="entityId"
                                inputValue={props.fields.entityId.editable
                                    ? entityIdInput
                                    : props.fields.entityId.displayValue ?? props.fields.entityId.value}
                                onInputChange={onInputChange}
                            />
                        )}
                        {props.fields.name && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.name}
                                fieldName="name"
                                inputValue={props.fields.name.editable
                                    ? nameInput
                                    : props.fields.name.displayValue ?? props.fields.name.value}
                                onInputChange={onInputChange}
                            />
                        )}
                        {props.fields.description && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                multiline
                                compProps={props}
                                inputProps={props.fields.description}
                                fieldName="description"
                                inputValue={props.fields.description.editable
                                    ? descriptionInput
                                    : props.fields.description.displayValue ?? props.fields.description.value}
                                onInputChange={onInputChange}
                            />
                        )}
                        {(!props.fields.iconFileName.hidden || !props.fields.iconUrl.hidden) && (
                            <ModeFormInputSection
                                className={clsx(formClasses.formInputGroup)}
                                collapsible={false}
                                title={props.texts.iconGroupTitle}
                                icon="image"
                            >
                                {props.fields.iconFileName && (
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        compProps={props}
                                        inputProps={props.fields.iconFileName}
                                        fieldName="iconFileName"
                                        inputValue={props.fields.iconFileName.editable
                                            ? homeIdInput : props.fields.iconFileName.displayValue ?? props.fields.iconFileName.value}
                                        onInputChange={onInputChange}
                                    />
                                )}
                                {props.fields.iconUrl && (
                                    <ModeInfoCompImageInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.iconUrl}
                                        fieldName="iconUrl"
                                        fileFilters=".jpg, .png, .jpeg, .gif, .bmp, .tif, .tiff|image/*"
                                        inputValue={iconUrlInput}
                                        onInputChange={onInputChange}
                                    />
                                )}
                            </ModeFormInputSection>
                        )}
                        {(!props.fields.imageFileName.hidden || !props.fields.imageUrl.hidden) && (
                            <ModeFormInputSection
                                className={clsx(formClasses.formInputGroup)}
                                collapsible={false}
                                title={props.texts.imageGroupTitle}
                                icon="image"
                            >
                                {props.fields.imageFileName && (
                                    <ModeInfoCompTextInputField
                                        className={formClasses.formInputField}
                                        compProps={props}
                                        inputProps={props.fields.imageFileName}
                                        fieldName="imageFileName"
                                        inputValue={props.fields.imageFileName.editable
                                            ? homeIdInput : props.fields.imageFileName.displayValue ?? props.fields.imageFileName.value}
                                        onInputChange={onInputChange}
                                    />
                                )}
                                {props.fields.imageUrl && (
                                    <ModeInfoCompImageInputField
                                        className={formClasses.formInputField}
                                        inputType="text"
                                        compProps={props}
                                        inputProps={props.fields.imageUrl}
                                        fieldName="imageUrl"
                                        fileFilters=".jpg, .png, .jpeg, .gif, .bmp, .tif, .tiff|image/*"
                                        inputValue={imageUrlInput}
                                        onInputChange={onInputChange}
                                    />
                                )}
                            </ModeFormInputSection>
                        )}
                        {props.fields.attributes && (
                            <ModeFormInputSection
                                className={clsx(formClasses.formInputGroup)}
                                collapsible={false}
                                title={props.texts.attributesGroupTitle}
                                icon="format_list_bulleted"
                            >
                                {Object.entries(props.fields.attributes).map(([key, inputField]) => {
                                    const onFormInputChangeHandler = (fieldName, value) => {
                                        if (props.onFormInputChange) {
                                            props.onFormInputChange(fieldName, value, true);
                                        }
                                    };
                                    const onFieldClickedHandler = (fieldName) => {
                                        if (props.onFieldClicked) {
                                            props.onFieldClicked(fieldName, true);
                                        }
                                    };

                                    if (inputField?.controlType === FieldInputType.CHECKBOX) {
                                        return (
                                            <ModeInfoCompCheckBoxInputField
                                                key={key}
                                                className={formClasses.formInputField}
                                                compProps={{
                                                    ...props,
                                                    onFormInputChange: onFormInputChangeHandler,
                                                    onFieldClicked   : onFieldClickedHandler,
                                                }}
                                                inputProps={inputField}
                                                fieldName={key}
                                                inputValue={attributeInputs?.[key] ? Boolean(attributeInputs[key]) : undefined}
                                                onInputChange={onAttributeInputChange}
                                            />
                                        );
                                    }
                                    if (inputField?.controlType === FieldInputType.TEXT_AREA) {
                                        return (
                                            <ModeInfoCompTextAreaInputField
                                                key={key}
                                                className={formClasses.formInputField}
                                                compProps={{
                                                    ...props,
                                                    onFormInputChange: onFormInputChangeHandler,
                                                    onFieldClicked   : onFieldClickedHandler,
                                                }}
                                                inputProps={inputField}
                                                fieldName={key}
                                                inputValue={attributeInputs?.[key] as string}
                                                rows={5}
                                                onInputChange={onAttributeInputChange}
                                            />
                                        );
                                    }
                                    if (inputField?.options && inputField?.options.length > 0) {
                                        return (
                                            <ModeInfoCompSelectInputField
                                                key={key}
                                                className={formClasses.formInputField}
                                                compProps={{
                                                    ...props,
                                                    onFormInputChange: onFormInputChangeHandler,
                                                    onFieldClicked   : onFieldClickedHandler,
                                                }}
                                                inputProps={inputField}
                                                fieldName={key}
                                                inputValue={attributeInputs?.[key]}
                                                onInputChange={onAttributeInputChange}
                                            />
                                        );
                                    }
                                    return (
                                        <ModeInfoCompTextInputField
                                            key={key}
                                            className={formClasses.formInputField}
                                            compProps={{
                                                ...props,
                                                onFormInputChange: onFormInputChangeHandler,
                                                onFieldClicked   : onFieldClickedHandler,
                                            }}
                                            inputProps={inputField}
                                            fieldName={key}
                                            inputType={inputField?.inputType}
                                            inputValue={attributeInputs?.[key]}
                                            onInputChange={onAttributeInputChange}
                                        />
                                    );
                                })}
                            </ModeFormInputSection>
                        )}
                        <ModeInfoCompAutoCompleteInputField
                            className={formClasses.formInputField}
                            fieldName="timeZone"
                            compProps={props}
                            inputProps={props.fields.timeZone}
                            inputValue={props.fields.timeZone.editable
                                ? timeZoneInput : props.fields.timeZone.displayValue ?? props.fields.timeZone.value}
                            onInputChange={onInputChange}
                        />
                        {props.fields.createdAt && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.createdAt}
                                fieldName="createdAt"
                                inputValue={props.fields.createdAt.displayValue ?? props.fields.createdAt.value}
                                onInputChange={onInputChange}
                            />
                        )}
                        {props.fields.updatedAt && (
                            <ModeInfoCompTextInputField
                                className={formClasses.formInputField}
                                inputType="text"
                                compProps={props}
                                inputProps={props.fields.updatedAt}
                                fieldName="updatedAt"
                                inputValue={props.fields.updatedAt.displayValue ?? props.fields.updatedAt.value}
                                onInputChange={onInputChange}
                            />
                        )}
                    </div>
                    {props.isEditing && props.actions && (
                        <div className={clsx(formClasses.formActionContainer)}>
                            <div />
                            <div>
                                {props.actions?.cancelEdit && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        size="small"
                                        startIcon={props.actions.cancelEdit.icon
                                            ? <FontIcon iconName={props.actions.cancelEdit.icon} />
                                            : undefined}
                                        onClick={() => {
                                            reverseFormInput();
                                            if (props.actions?.cancelEdit) {
                                                props.actions.cancelEdit.onClick();
                                            }
                                        }}
                                    >
                                        {props.actions.cancelEdit.label}
                                    </Button>
                                )}
                                {props.actions?.saveUpdate && (
                                    <Button
                                        className={clsx(formClasses.formActionButton)}
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        size="small"
                                        startIcon={props.actions.saveUpdate.icon
                                            ? <FontIcon iconName={props.actions.saveUpdate.icon} />
                                            : undefined}
                                    >
                                        {props.actions.saveUpdate.label}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </div>

        </Paper>
    );

};
